/* Native Shopify cart drawer with a strictly no-purchase Tailscale demo mode.
 * Shopify Ajax Cart API: /cart.js, /cart/add.js, /cart/change.js.
 * Source of truth is Shopify's session cart. No local mock orders or invented totals.
 * Capi-Europe rhythm: purposeful 160ms icon/quantity and 240ms drawer movement.
 */
(() => {
  'use strict';
  const drawer = document.querySelector('[data-cart-drawer]');
  if (!(drawer instanceof HTMLDialogElement)) return;
  const isDemo = drawer.dataset.cartMode !== 'shopify';
  const reducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const panel = drawer.querySelector('[data-cart-panel]');
  const badge = () => document.querySelector('[data-cart-badge]');
  const countLabel = drawer.querySelector('[data-cart-count]');
  const empty = drawer.querySelector('[data-cart-empty]');
  const list = drawer.querySelector('[data-cart-items]');
  const footer = drawer.querySelector('[data-cart-footer]');
  const total = drawer.querySelector('[data-cart-total]');
  const loading = drawer.querySelector('[data-cart-loading]');
  const error = drawer.querySelector('[data-cart-error]');
  const closeButton = drawer.querySelector('[data-cart-close]');
  const root = () => {
    const value = window.Shopify?.routes?.root || drawer.dataset.cartRoot || '/';
    return value.endsWith('/') ? value : value + '/';
  };
  const endpoint = (name) => root() + name;
  const currency = () => window.Shopify?.currency?.active || 'EUR';
  const format = (cents) => {
    try {
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: currency() }).format((Number(cents) || 0) / 100);
    } catch (_) {
      return '€ ' + ((Number(cents) || 0) / 100).toFixed(2).replace('.', ',');
    }
  };

  let opener = null;
  let cachedCart = null;
  let updating = false;
  let closing = false;
  let progress = null;

  function setError(message = '') {
    if (!error) return;
    error.hidden = !message;
    error.textContent = message;
  }
  function setLoading(visible) {
    if (loading) loading.hidden = !visible;
    if (visible && list) list.hidden = true;
    if (visible && empty) empty.hidden = true;
    if (visible && footer) footer.hidden = true;
  }
  function bump(element, keyframes, duration = 160) {
    if (!element?.animate || reducedMotion()) return;
    element.animate(keyframes, { duration, easing: 'cubic-bezier(.22,1,.36,1)' });
  }

  function updateHeader(count) {
    const integer = Math.max(0, Number(count) || 0);
    const trigger = document.querySelector('[data-cart-open]');
    if (trigger) trigger.setAttribute('aria-label', 'Winkelwagen: ' + integer + (integer === 1 ? ' artikel' : ' artikelen'));
    const bubble = badge();
    if (bubble) {
      bubble.textContent = String(integer);
      bubble.hidden = integer === 0;
      if (integer > 0) bump(bubble, [
        { transform: 'scale(.72)', opacity: .5 },
        { transform: 'scale(1.14)', opacity: 1, offset: .65 },
        { transform: 'scale(1)', opacity: 1 }
      ]);
    }
    if (countLabel) countLabel.textContent = integer + (integer === 1 ? ' artikel' : ' artikelen');
  }

  function element(tag, className, text) {
    const node = document.createElement(tag);
    if (className) node.className = className;
    if (text !== undefined) node.textContent = String(text);
    return node;
  }
  function safeUrl(raw) {
    if (typeof raw !== 'string') return '';
    if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
    if (raw.startsWith('https://')) return raw;
    return '';
  }

  function createLine(line) {
    const li = element('li', 'lp-cart-drawer__item');
    const media = element('a', 'lp-cart-drawer__item-image');
    media.href = safeUrl(line.url) || root() + 'cart';
    let approvedImage;
    try { approvedImage = JSON.parse(document.getElementById('lp-approved-cart-images')?.textContent || '{}')[String(line.product_id)]; } catch (_) {}
    const imgUrl = safeUrl(approvedImage?.url || line.image || line.featured_image?.url || '');
    if (approvedImage) media.classList.add('lp-approved-cart-image');
    if (imgUrl) {
      const img = element('img');
      img.src = imgUrl; img.alt = approvedImage?.serviceNote ? (line.product_title || line.title || '') + ' — ' + approvedImage.serviceNote : ''; img.loading = 'lazy';
      media.append(img);
    } else {
      media.classList.add('lp-cart-drawer__item-image--empty');
      media.textContent = 'Foto volgt';
    }

    const details = element('div', 'lp-cart-drawer__item-details');
    const title = element('a', 'lp-cart-drawer__item-title', line.product_title || line.title || 'Product');
    title.href = media.href;
    details.append(title);
    if (approvedImage?.serviceNote) {
      details.append(element('span', 'lp-service-badge', approvedImage.serviceLabel));
      details.append(element('span', 'lp-service-note', approvedImage.serviceNote));
    } else if (approvedImage?.illustrative) details.append(element('span', 'lp-approved-cart-label', 'Voorbeeldbeeld'));
    if (line.variant_title && line.variant_title !== 'Default Title') {
      details.append(element('span', 'lp-cart-drawer__item-variant', line.variant_title));
    }
    const price = element('strong', 'lp-cart-drawer__item-price',
      format(line.final_line_price ?? line.line_price ?? 0));
    details.append(price);

    const controls = element('div', 'lp-cart-drawer__item-controls');
    const quantity = element('div', 'lp-cart-drawer__stepper');
    const minus = element('button', '', '−');
    minus.type = 'button'; minus.dataset.cartChange = '-1'; minus.dataset.lineKey = String(line.key);
    minus.setAttribute('aria-label', 'Verminder aantal van ' + (line.product_title || line.title || 'product'));
    const value = element('span', '', String(line.quantity || 0));
    value.setAttribute('aria-label', 'Aantal ' + String(line.quantity || 0));
    const plus = element('button', '', '+');
    plus.type = 'button'; plus.dataset.cartChange = '1'; plus.dataset.lineKey = String(line.key);
    plus.setAttribute('aria-label', 'Verhoog aantal van ' + (line.product_title || line.title || 'product'));
    quantity.append(minus, value, plus);
    const remove = element('button', 'lp-cart-drawer__remove', 'Verwijder');
    remove.type = 'button'; remove.dataset.cartChange = 'remove'; remove.dataset.lineKey = String(line.key);
    remove.setAttribute('aria-label', 'Verwijder ' + (line.product_title || line.title || 'product'));
    controls.append(quantity, remove);
    details.append(controls);
    li.append(media, details);
    return li;
  }
  function render(cart) {
    cachedCart = cart;
    const items = Array.isArray(cart.items) ? cart.items : [];
    const itemCount = Number(cart.item_count ?? items.reduce((a, i) => a + i.quantity, 0)) || 0;
    updateHeader(itemCount);
    document.dispatchEvent(new CustomEvent('leonparc:cart-updated', { detail: { cart } }));
    if (list) {
      const fragment = document.createDocumentFragment();
      items.forEach((line) => fragment.append(createLine(line)));
      list.replaceChildren(fragment);
      list.hidden = items.length === 0;
    }
    if (empty) empty.hidden = items.length > 0;
    if (footer) footer.hidden = items.length === 0;
    if (total) total.textContent = format(cart.total_price ?? 0);
    setLoading(false);
    bump(list, [{ opacity: .7, transform: 'translateY(7px)' }, { opacity: 1, transform: 'translateY(0)' }], 240);
  }

  async function getJson(url, options = {}) {
    const response = await fetch(url, {
      ...options,
      credentials: 'same-origin',
      headers: { Accept: 'application/json', ...(options.headers || {}) }
    });
    const content = await response.json().catch(() => ({}));
    if (!response.ok) {
      const description = typeof content.description === 'string' ? content.description : '';
      const message = description && description.length <= 170 ? description : 'De winkelwagen kon niet worden bijgewerkt. Probeer opnieuw.';
      throw new Error(message);
    }
    return content;
  }
  async function refreshCart() {
    if (isDemo) return;
    setError();
    setLoading(true);
    try {
      const data = await getJson(endpoint('cart.js'));
      render(data);
    } catch (caught) {
      setLoading(false);
      setError('Kan de winkelwagen niet ophalen. Gebruik eventueel de gewone winkelwagenpagina.');
      if (cachedCart) render(cachedCart);
      else {
        if (empty) empty.hidden = false;
        if (footer) footer.hidden = true;
      }
    }
  }

  function openDrawer(source) {
    if (source) opener = source;
    if (closing) return;
    if (!drawer.open) {
      drawer.showModal();
      document.documentElement.dataset.leonparcCartOpen = 'true';
      closeButton?.focus({ preventScroll: true });
      bump(panel, [
        { transform: 'translateX(38px)', opacity: .84 },
        { transform: 'translateX(0)', opacity: 1 }
      ], 240);
      bump(document.querySelector('[data-cart-open] .lp-icon'), [
        { transform: 'translateY(2px) rotate(6deg)' },
        { transform: 'translateY(0) rotate(0)' }
      ]);
    }
    if (isDemo) {
      setError();
      setLoading(false);
      updateHeader(0);
      if (empty) empty.hidden = false;
      if (list) list.hidden = true;
      if (footer) footer.hidden = true;
    } else refreshCart();
  }

  function closeDrawer() {
    if (!drawer.open || closing) return;
    if (reducedMotion() || !panel?.animate) { drawer.close(); return; }
    closing = true;
    progress?.cancel();
    progress = panel.animate([
      { transform: 'translateX(0)', opacity: 1 },
      { transform: 'translateX(38px)', opacity: .75 }
    ], { duration: 180, easing: 'cubic-bezier(.22,1,.36,1)' });
    progress.finished.then(() => { closing = false; drawer.close(); },
      () => { closing = false; if (drawer.open) drawer.close(); });
  }

  drawer.addEventListener('cancel', (event) => {
    if (reducedMotion()) return;
    event.preventDefault();
    closeDrawer();
  });
  drawer.addEventListener('close', () => {
    document.documentElement.removeAttribute('data-leonparc-cart-open');
    closing = false;
    progress?.cancel();
    const focus = opener;
    if (focus?.isConnected && !focus.closest('[hidden]')) focus.focus({ preventScroll: true });
    opener = null;
  });

  document.addEventListener('click', (event) => {
    if (!(event.target instanceof Element)) return;
    const open = event.target.closest('a[data-cart-open]');
    if (open) {
      event.preventDefault();
      openDrawer(open);
      return;
    }
    const close = event.target.closest('button[data-cart-close]');
    if (close && drawer.contains(close)) {
      closeDrawer();
      return;
    }
    if (event.target === drawer) {
      const rect = panel?.getBoundingClientRect();
      if (rect && (event.clientX < rect.left || event.clientX > rect.right ||
          event.clientY < rect.top || event.clientY > rect.bottom)) closeDrawer();
      return;
    }
    const change = event.target.closest('[data-cart-change]');
    if (!change || !drawer.contains(change) || isDemo || updating || !cachedCart) return;
    const key = change.dataset.lineKey;
    const line = cachedCart.items?.find(item => String(item.key) === key);
    if (!line) return;
    const direction = change.dataset.cartChange;
    const target = direction === 'remove' ? 0 : Math.max(0, line.quantity + Number(direction));
    if (!Number.isInteger(target) || target > 999 || target === line.quantity) return;
    updateLine(key, target);
  });

  async function updateLine(id, quantity) {
    const focused = document.activeElement?.matches?.('[data-cart-change]')
      ? { key: document.activeElement.dataset.lineKey, action: document.activeElement.dataset.cartChange }
      : null;
    updating = true;
    setError();
    drawer.querySelectorAll('[data-cart-change]').forEach(button => { button.disabled = true; });
    try {
      const data = await getJson(endpoint('cart/change.js'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id, quantity })
      });
      render(data);
    } catch (caught) {
      const message = caught.message || 'Aantal aanpassen is niet gelukt.';
      // The platform may have changed quantities despite the error; reconcile.
      await refreshCart();
      setError(message);
    } finally {
      updating = false;
      drawer.querySelectorAll('[data-cart-change]').forEach(button => { button.disabled = false; });
      if (focused) {
        const match = [...drawer.querySelectorAll('[data-cart-change]')].find(button =>
          button.dataset.lineKey === focused.key && button.dataset.cartChange === focused.action);
        if (match) match.focus({ preventScroll: true });
        else closeButton?.focus({ preventScroll: true });
      }
    }
  }

  if (!isDemo) document.querySelectorAll('[data-blu-eye-addon-label]').forEach(label => { label.hidden = false; });

  document.addEventListener('submit', async (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || isDemo) return;
    const action = new URL(form.action, location.href);
    if (action.origin !== location.origin || !/(?:^|\/)cart\/add(?:\.js)?\/?$/.test(action.pathname)) return;
    if (!form.reportValidity()) return;
    event.preventDefault();
    if (form.dataset.cartPending === 'true') return;
    const submit = event.submitter;
    if (submit?.disabled) return;
    form.dataset.cartPending = 'true';
    if (submit instanceof HTMLButtonElement) submit.disabled = true;
    const originalText = submit?.querySelector('[data-add-product-label]')?.textContent;
    const feedback = form.querySelector('[data-cart-add-error]') || element('p', 'lp-cart-add-error');
    feedback.dataset.cartAddError = '';
    feedback.setAttribute('role', 'alert');
    feedback.hidden = true;
    if (!feedback.isConnected) form.append(feedback);
    try {
      const payload = new FormData(form);
      const accessory = form.querySelector('[data-blu-eye-addon]');
      if (accessory instanceof HTMLInputElement && accessory.checked && /^\d+$/.test(accessory.dataset.variantId || '')) {
        const quantity = Number(payload.get('quantity') || 1);
        const mainId = String(payload.get('id') || '');
        if (!/^\d+$/.test(mainId) || !Number.isInteger(quantity) || quantity < 1) throw new Error('Controleer het gekozen product en aantal.');
        payload.delete('id');
        payload.delete('quantity');
        payload.set('items[0][id]', mainId);
        payload.set('items[0][quantity]', String(quantity));
        payload.set('items[1][id]', accessory.dataset.variantId);
        payload.set('items[1][quantity]', String(quantity));
      }
      await getJson(endpoint('cart/add.js'), { method: 'POST', body: payload });
      openDrawer(submit || form);
    } catch (caught) {
      feedback.textContent = caught.message || 'Toevoegen is niet gelukt. Probeer het opnieuw.';
      feedback.hidden = false;
    } finally {
      delete form.dataset.cartPending;
      if (submit instanceof HTMLButtonElement) submit.disabled = false;
      const label = submit?.querySelector('[data-add-product-label]');
      if (label && originalText) label.textContent = originalText;
    }
  });
})();
